import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@/lib/utils";

/**
 * Slider del DS. Soporta UNA o VARIAS manijas: la cantidad la decide el largo
 * de `value`, así que el mismo componente sirve para un tope simple y para un
 * rango de dos extremos.
 *
 * Venía de shadcn sin estilar y sin ningún uso en la aplicación. Lo que cambió
 * al traerlo al DS:
 *
 *   * el relleno usa el verde de marca y la pista el gris de borde, en vez de
 *     `bg-muted`, que en oscuro quedaba casi invisible;
 *   * la manija va en `bg-background` y no en blanco fijo — en modo oscuro un
 *     blanco quemado es la única pieza que no acompaña al tema;
 *   * el foco y el hover son el HALO del DS (`ring-[3px]` del color del
 *     control), no el anillo genérico de shadcn.
 *
 * Los grosores van en px a propósito: son líneas y ajustes ópticos, que es la
 * excepción declarada a la regla de no usar px.
 */
function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root>) {
  const _values = React.useMemo(
    () =>
      Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max],
    [value, defaultValue, min, max],
  );

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50",
        "data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          "bg-border relative grow overflow-hidden rounded-full",
          "data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full",
          "data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5",
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn(
            "bg-primary absolute",
            "data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full",
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: _values.length }, (_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={index}
          className={cn(
            "border-primary bg-background block size-4 shrink-0 rounded-full border-[1.5px] shadow-sm",
            "transition-[box-shadow] outline-hidden",
            "hover:ring-[3px] hover:ring-[color-mix(in_oklab,var(--color-primary)_22%,transparent)]",
            "focus-visible:ring-[3px] focus-visible:ring-[color-mix(in_oklab,var(--color-primary)_22%,transparent)]",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider };
